import React, { useState, useEffect, useCallback, useMemo, useRef } from "react"; // Added useMemo
import { useNavigate, Link } from "react-router-dom";
import {
  Search,
  TrendingUp,
  MessageSquare,
  Filter,
  ChevronDown,
  Bookmark,
  ArrowRight,
  Clock,
  ChevronUp,
  RefreshCw,
  AlertCircle,
  Pencil,
  X,
  Info,
  Sparkles,
  Loader2,
  CheckCircle,
  ChevronRight,
  Zap,
  Tag,
  Tags,
  AlertTriangle,
  Target,
  BarChart,
  Lightbulb,
  Compass,
  Disc,
  FileText,
  Users,
  Lock,
  Star,
} from "lucide-react"; // Ensure all used icons are imported
import axios from "./axiosConfig";
import { motion, AnimatePresence } from "framer-motion";
import { formatDistanceToNow } from "date-fns";
import { findHighestRatedInsight } from "./InsightCard";
import EnhancedHeader from "./EnhancedHeader";

// --- Child Components ---
import { InsightCard } from "./InsightCard";
import { PersonasAggregateCard } from "./PersonasAggregateCard";
// Assume UpgradeModal and BusinessPlanCTA are also defined/imported correctly

const BusinessPlanCTA = ({ insight }) => (
  <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="mt-6 border-t border-gray-100 pt-6">
    <div className="bg-gradient-to-r from-blue-50 to-emerald-50 rounded-xl p-6">
      <div className="flex items-start space-x-4">
        <div className="flex-shrink-0">
          <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center">
            <FileText className="w-6 h-6 text-blue-600" />
          </div>
        </div>
        <div className="flex-1">
          <h4 className="text-lg font-semibold text-blue-900">Ready to Launch This Business?</h4>
          <p className="text-blue-700 mt-2 mb-4">
            Generate your complete launch kit with market analysis, financial projections, and step-by-step execution plan.
          </p>
          <div className="space-y-3">
            <div className="flex items-center text-sm text-gray-600">
              <CheckCircle className="w-4 h-4 mr-2 text-emerald-500" />
              Market Size & Competitor Analysis
            </div>
            <div className="flex items-center text-sm text-gray-600">
              <CheckCircle className="w-4 h-4 mr-2 text-emerald-500" />
              Financial Projections & Funding Strategy
            </div>
            <div className="flex items-center text-sm text-gray-600">
              <CheckCircle className="w-4 h-4 mr-2 text-emerald-500" />
              Complete Marketing & Launch Plan
            </div>
          </div>
          <Link
            to="/report"
            onClick={() => {
              localStorage.setItem(
                "businessPlanInput",
                `${insight.details?.validated_idea || ""}\n\n` +
                  `Problem: ${insight.details?.core_problem || ""}\n` +
                  `Market Gap: ${insight.details?.underserved_gap || ""}\n` +
                  `Evidence: ${insight.details?.problem_evidence || ""}\n` +
                  `Solution: ${insight.details?.solution_concept || ""}\n` +
                  `Strategy: ${insight.details?.strategic_angle || ""}\n` +
                  `Customer Voice: ${insight.details?.voice_of_customer || ""}`
              );
            }}
            className="mt-6 inline-flex items-center justify-center w-full bg-gradient-to-r from-blue-600 to-blue-700 text-white py-3 px-6 rounded-xl font-semibold hover:from-blue-700 hover:to-blue-800 transition-all duration-300 shadow-md hover:shadow-lg group"
          >
            <FileText className="w-5 h-5 mr-2 group-hover:scale-110 transition-transform" />
            Generate Launch Kit Now
            <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
          </Link>
        </div>
      </div>
    </div>
  </motion.div>
);
const UpgradeModal = ({ show, onClose, searchTopic = "" }) => {
  if (!show) return null;

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-xl max-w-md w-full p-6 shadow-xl relative overflow-hidden">
        {/* Decorative elements */}
        <div className="absolute -top-20 -right-20 w-40 h-40 bg-emerald-100 rounded-full opacity-50"></div>
        <div className="absolute -bottom-20 -left-20 w-40 h-40 bg-blue-100 rounded-full opacity-50"></div>

        <div className="relative z-10">
          <div className="text-center mb-6">
            <div className="w-20 h-20 bg-gradient-to-br from-emerald-400 to-emerald-600 rounded-full flex items-center justify-center mx-auto mb-4 shadow-md">
              <Zap size={28} className="text-white" />
            </div>
            <h3 className="text-2xl font-bold text-gray-900">Unlock All Insights</h3>
            <p className="text-gray-600 mt-2">
              {searchTopic
                ? `Get complete analysis and strategic guidance for "${searchTopic}" and all future searches.`
                : "Get complete analysis and strategic guidance for all your market opportunities."}
            </p>
          </div>

          <div className="space-y-4 mb-6 bg-gray-50 rounded-xl p-4 border border-gray-100">
            <div className="flex items-start">
              <div className="bg-emerald-100 rounded-full p-1 mt-0.5 mr-3 flex-shrink-0">
                <CheckCircle className="w-4 h-4 text-emerald-600" />
              </div>
              <p className="text-gray-700 text-sm">Access all insights with complete market validation</p>
            </div>
            <div className="flex items-start">
              <div className="bg-emerald-100 rounded-full p-1 mt-0.5 mr-3 flex-shrink-0">
                <CheckCircle className="w-4 h-4 text-emerald-600" />
              </div>
              <p className="text-gray-700 text-sm">Get detailed solution concepts and strategic action plans</p>
            </div>
            <div className="flex items-start">
              <div className="bg-emerald-100 rounded-full p-1 mt-0.5 mr-3 flex-shrink-0">
                <CheckCircle className="w-4 h-4 text-emerald-600" />
              </div>
              <p className="text-gray-700 text-sm">Generate complete business plans and implementation guides</p>
            </div>
          </div>

          <div className="flex flex-col gap-3">
            <Link
              to="/credits/"
              className="bg-gradient-to-r from-emerald-500 to-emerald-600 text-white py-3.5 rounded-xl font-medium text-center shadow hover:brightness-105 transition-all duration-300 flex items-center justify-center group"
            >
              <span>Upgrade Now</span>
              <ChevronRight
                className="ml-2 opacity-0 group-hover:opacity-100 group-hover:translate-x-1 transition-all duration-300"
                size={18}
              />
            </Link>
            <button onClick={onClose} className="text-gray-500 hover:text-gray-700 py-2.5 font-medium transition-colors">
              Maybe Later
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
// --- Main Component ---
const InsightsTab = () => {
  // --- State Variables ---
  const [isNewUser, setIsNewUser] = useState(null);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [userDetails, setUserDetails] = useState(null);
  const [monitoringConfig, setMonitoringConfig] = useState({ keywords: "" });
  const [isGeneratingInsights, setIsGeneratingInsights] = useState(false);
  const [generationProgress, setGenerationProgress] = useState(0);
  const [rawDisplayItems, setRawDisplayItems] = useState([]); // <-- Holds flat list from API
  const [nextPageUrl, setNextPageUrl] = useState(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const [expandedCards, setExpandedCards] = useState(new Set()); // <-- Use Set for unique keys
  const [currentPlan, setCurrentPlan] = useState("Free");
  const [showKeywordModal, setShowKeywordModal] = useState(false);
  const [bookmarkedPainPoints, setBookmarkedPainPoints] = useState(new Set());
  const [insightCredits, setInsightCredits] = useState(0);
  const [loading, setLoading] = useState(true); // For initial load
  const [error, setError] = useState(null);
  const navigate = useNavigate();
  const insightsSectionRef = useRef(null); // Ref for scrolling
  const [justGenerated, setJustGenerated] = useState(false);
  const [isPremium, setIsPremium] = useState(false);
  const suggestedKeywords = ["fitness tech", "remote work tools", "sustainable packaging"]; // Example

  const fetchUserDetails = useCallback(async () => {
    console.log("Fetching user details...");
    try {
      const response = await axios.get("/get-user-credits/");
      console.log("User details response:", response.data);
      setUserDetails(response.data);

      // FIX: Consider a user premium only if they have an active subscription AND are not on the Free plan
      setIsPremium(response.data.subscription_active && response.data.plan_name !== "Free");

      setCurrentPlan(response.data.plan_name || "Free");
      setInsightCredits(response.data.ideation_credits || 0);

      // Check if user has accessed preview ONLY IF they are not premium/subscribed
      const userIsEffectivelyNew =
        (response.data.plan_name === "Free" || !response.data.subscription_active) && !response.data.accessed_ideation_free_preview;
      setIsNewUser(userIsEffectivelyNew);
    } catch (error) {
      console.error("Failed to fetch user details:", error);
      setError("Failed to load user data. Please refresh.");
      setIsNewUser(false);
    }
  }, []);

  const handleToggleBookmark = useCallback(
    async (itemId) => {
      const item = rawDisplayItems.find((i) => i.id === itemId);
      if (!item || item.details?.type === "personas") return; // No bookmark for persona block

      const isBookmarked = bookmarkedPainPoints.has(itemId);
      // Optimistic update
      setBookmarkedPainPoints((prev) => {
        const newSet = new Set(prev);
        if (isBookmarked) {
          newSet.delete(itemId);
        } else {
          newSet.add(itemId);
        }
        return newSet;
      });

      try {
        await axios.post(`/toggle-bookmark/${itemId}/`);
      } catch (error) {
        console.error("Error toggling bookmark:", error);
        // Revert optimistic update on error
        setBookmarkedPainPoints((prev) => {
          const newSet = new Set(prev);
          if (isBookmarked) {
            newSet.add(itemId);
          } else {
            newSet.delete(itemId);
          }
          return newSet;
        });
        // Add user feedback (e.g., toast)
      }
    },
    [rawDisplayItems, bookmarkedPainPoints]
  ); // Include dependencies

  const toggleExpand = useCallback((key) => {
    setExpandedCards((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(key)) {
        newSet.delete(key);
      } else {
        newSet.add(key);
      }
      return newSet;
    });
  }, []); // No dependencies needed

  const fetchUserInsights = useCallback(
    async (url = "/fetch-user-insights/") => {
      const isPrimaryFetch = url === "/fetch-user-insights/";
      // Loading state is set *before* this function is called for primary fetch (in handleNewInsight)
      // Only set loadingMore for subsequent fetches
      if (!isPrimaryFetch) {
        console.log(`[Load More] STARTING for ${url}`);
        setLoadingMore(true); // Set loadingMore state here
      } else {
        console.log("[Primary Fetch] Fetching initial page...");
        // Do not set loading=true here, it's set by the caller (handleNewInsight or initial useEffect)
      }

      try {
        console.log(`[${isPrimaryFetch ? "Primary" : "Load More"}] Attempting GET ${url}`);
        const response = await axios.get(url);
        const responseData = response.data;
        console.log(`[${isPrimaryFetch ? "Primary" : "Load More"}] Raw API Response received.`);

        // Assuming backend sends { count, next, previous, results: { monitored_keywords, items } }
        const resultsData = responseData.results || {};
        const newRawItems = resultsData.items || [];
        const currentMonitoredKeywords = resultsData.monitored_keywords || [];

        console.log(`[${isPrimaryFetch ? "Primary" : "Load More"}] Found ${newRawItems.length} raw items in response.`);

        // --- FIXED State Update Logic ---
        if (isPrimaryFetch) {
          console.log(`[Primary Fetch] REPLACING state with ${newRawItems.length} items.`);
          // *** REPLACE State Directly ***
          setRawDisplayItems(newRawItems);
        } else if (newRawItems.length > 0) {
          // Append for Load More only if new items exist
          console.log(`[Load More] Appending ${newRawItems.length} new items.`);
          setRawDisplayItems((prev) => {
            const existingIds = new Set(prev.map((item) => item.id));
            const uniqueNewItems = newRawItems.filter((item) => !existingIds.has(item.id));
            console.log(`[Load More] Adding ${uniqueNewItems.length} unique items.`);
            return [...prev, ...uniqueNewItems];
          });
        }
        // --- End State Update Logic ---

        // Update next page URL from top level
        // const nextUrl = responseData.next || null;
        // console.log(`[${isPrimaryFetch ? "Primary" : "Load More"}] Setting next page URL to:`, nextUrl);
        // setNextPageUrl(nextUrl);

        const nextUrl = responseData.next || null;
        // Fix mixed content by ensuring HTTPS
        const fixedNextUrl = nextUrl ? nextUrl.replace("http://", "https://") : null;
        console.log(`[${isPrimaryFetch ? "Primary" : "Load More"}] Setting next page URL to fixed:`, fixedNextUrl);
        setNextPageUrl(fixedNextUrl);

        // Update display of currently monitored keywords from results
        // Avoid overwriting if user changed input while fetch was happening? Check if needed.
        // For simplicity, always update based on response here.
        console.log(`[${isPrimaryFetch ? "Primary" : "Load More"}] Setting monitored keywords display to:`, currentMonitoredKeywords);
        setMonitoringConfig((prev) => ({
          ...prev,
          keywords: Array.isArray(currentMonitoredKeywords) ? currentMonitoredKeywords.join(", ") : String(currentMonitoredKeywords || ""),
        }));
      } catch (err) {
        console.error(`[${isPrimaryFetch ? "Primary" : "Load More"}] ERROR during fetch:`, err);
        if (isPrimaryFetch) {
          setError("Failed to load insights. Please check console.");
          setRawDisplayItems([]); // Clear items on primary fetch error
        }
        // Handle load more error if needed (e.g., show a toast)
      } finally {
        // Reset relevant loading states
        if (isPrimaryFetch) setLoading(false); // Reset main loading only on primary fetch
        setLoadingMore(false); // Always reset loadingMore
        console.log(`[${isPrimaryFetch ? "Primary" : "Load More"}] FINISHED Fetch.`);
      }
      // Add setMonitoringConfig, setError to dependencies if they aren't guaranteed stable
    },
    [
      /* Add other stable dependencies if needed */
    ]
  );

  // --- Generation Handler ---
  const handleNewInsight = useCallback(async () => {
    // Add check: Don't proceed if user status isn't loaded yet
    if (isNewUser === null) {
      console.log("handleNewInsight: User status (isNewUser) not loaded yet. Aborting.");
      return; // Prevent running if state isn't ready
    }

    console.log(`handleNewInsight Check: isNewUser=<span class="math-inline">\{isNewUser\}, insightCredits\=</span>{insightCredits}`);

    // Use isNewUser === false to check if it's explicitly known they are not new
    // if (insightCredits <= 0 && isNewUser === false) {
    //   setShowUpgradeModal(true);
    //   return;
    // }

    // Condition to STOP: User is NOT new (preview used) AND they have NO credits

    if (!isPremium && isNewUser === false && insightCredits <= 0) {
      console.log("handleNewInsight: Denying access - Not Premium, Preview Used, AND No Credits."); // Updated log
      setShowUpgradeModal(true);
      return; // Stop execution
    }
    // If we reach here, user can proceed (is new OR has credits)
    console.log("handleNewInsight: Access GRANTED.");

    const keywords = monitoringConfig.keywords
      .split(",")
      .map((kw) => kw.trim())
      .filter(Boolean);
    if (keywords.length === 0) {
      alert("Please enter a topic first.");
      return;
    }

    console.log("handleNewInsight: Starting generation...");
    setIsGeneratingInsights(true);
    setGenerationProgress(0);
    setError(null);
    setLoading(true); // Set loading true as we are replacing all data
    // const progressInterval = setInterval(() => setGenerationProgress((p) => Math.min(p + 10, 95)), 300);

    const progressInterval = setInterval(() => {
      setGenerationProgress((prevProgress) => {
        // If below 30%, move quickly to show initial activity
        if (prevProgress < 30) {
          return Math.min(prevProgress + 3, 30);
        }
        // From 30-60%, slow down a bit
        else if (prevProgress < 60) {
          return Math.min(prevProgress + 1.5, 60);
        }
        // From 60-85%, slow down significantly - this is where most processing happens
        else if (prevProgress < 85) {
          return Math.min(prevProgress + 0.5, 85);
        }
        // From 85-95%, move very slowly - this is typically the longest wait
        else if (prevProgress < 95) {
          return Math.min(prevProgress + 0.2, 95);
        }
        // Never automatically reach 100% - that will be set when processing actually completes
        return prevProgress;
      });
    }, 300);

    let success = false;

    try {
      console.log("handleNewInsight: Posting to generate-insights...");
      const response = await axios.post("/generate-insights/", { interests: keywords });
      console.log("handleNewInsight: Post response status:", response.status);

      if (response.status === 200 || response.status === 202) {
        console.log("handleNewInsight: Generation successful. Clearing old results and fetching new.");
        await fetchUserDetails(); // Refresh user details (e.g., credits, preview flag)

        // // Clear old results before fetching new ones
        // setRawDisplayItems([]);
        // setNextPageUrl(null);
        // setExpandedCards(new Set());
        console.log("handleNewInsight: Clearing old results before new fetch...");
        setRawDisplayItems([]); // <-- **CRITICAL: Clear the raw items array**
        setNextPageUrl(null); // <-- **CRITICAL: Reset pagination**
        setExpandedCards(new Set()); // <-- **CRITICAL: Reset expanded cards**
        setError(null); // <-- Clear any previous fetch errors
        setLoading(true); // <-- Set loading for the upcoming fetch

        // Fetch the *first page* of the new/updated results
        await fetchUserInsights(); // This will set loading to false in its finally block

        setGenerationProgress(100);
        // setJustGenerated(true); // Trigger scroll
        // Replace the direct setJustGenerated(true) with:
        setTimeout(() => {
          setJustGenerated(true);
        }, 500); // Small delay to ensure data is rendered
        success = true;

        console.log("handleNewInsight: Insights generated/fetched successfully. Reloading page..."); // <-- REMOVE THIS LINE
        // window.location.reload(); // <--- REMOVE THIS LINE
      } else {
        setError(`Generation completed with status ${response.status}.`);
      }
    } catch (error) {
      // window.location.reload(); // <--- REMOVE THIS LINE
      success = false;
      console.error("handleNewInsight: Error caught:", error);
      setGenerationProgress(0);
      if (error.response?.status === 402) {
        setShowUpgradeModal(true);
      } else {
        setError("Result found but unable to display. Please reload the page");
      }
      setLoading(false); // Ensure loading is false on error if fetch didn't run
    } finally {
      clearInterval(progressInterval);
      setTimeout(() => setIsGeneratingInsights(false), success ? 500 : 0);
    }
  }, [monitoringConfig.keywords, insightCredits, isNewUser, fetchUserDetails, fetchUserInsights]); // Dependencies

  // --- Grouping and Sorting Logic ---
  const groupedAndSortedItems = useMemo(() => {
    console.log(`useMemo: Grouping ${rawDisplayItems.length} raw items...`);
    const groups = new Map();

    rawDisplayItems.forEach((item) => {
      const keywords = Array.isArray(item.query_keywords) && item.query_keywords.length > 0 ? item.query_keywords : ["unknown_topic"]; // Group items missing keywords separately
      const key = JSON.stringify(keywords.sort());

      if (!groups.has(key)) {
        groups.set(key, { items: [], latestDate: new Date(0) });
      }
      const group = groups.get(key);
      group.items.push(item);

      try {
        // Track latest date for sorting groups
        if (item.created_at) {
          const itemDate = new Date(item.created_at);
          if (!isNaN(itemDate.getTime()) && itemDate > group.latestDate) {
            group.latestDate = itemDate;
          }
        }
      } catch (e) {
        console.error("Date error during grouping:", e);
      }
    });

    const sortedGroups = Array.from(groups.entries()).sort(
      ([, groupA], [, groupB]) => groupB.latestDate.getTime() - groupA.latestDate.getTime()
    ); // Newest group first

    // Sort items within each group
    sortedGroups.forEach(([, group]) => {
      group.items.sort((a, b) => {
        const isAPersona = a.details?.type === "personas";
        const isBPersona = b.details?.type === "personas";
        if (isAPersona && !isBPersona) return -1; // Personas first
        if (!isAPersona && isBPersona) return 1;
        try {
          // Insights by date desc
          const dateA = a.created_at ? new Date(a.created_at) : new Date(0);
          const dateB = b.created_at ? new Date(b.created_at) : new Date(0);
          const timeA = !isNaN(dateA.getTime()) ? dateA.getTime() : 0;
          const timeB = !isNaN(dateB.getTime()) ? dateB.getTime() : 0;
          return timeB - timeA;
        } catch (e) {
          return 0;
        }
      });
    });

    console.log("useMemo: Final grouped/sorted structure generated.");
    return sortedGroups;
  }, [rawDisplayItems]);

  // --- Effects ---
  useEffect(() => {
    // Initial Fetch on Mount
    console.log("Initial mount: Fetching user details and first page of insights...");
    setLoading(true); // Set loading true for the combined initial load
    fetchUserDetails().then(() => {
      fetchUserInsights(); // This sets loading false inside its finally block
    });

    // Fetch bookmarks (run once)
    axios
      .get("/list-bookmarks/")
      .then((response) => setBookmarkedPainPoints(new Set(response.data.map((item) => item.id))))
      .catch((error) => console.error("Error fetching bookmarked items:", error));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Run only once on mount

  // Scroll after generation effect
  useEffect(() => {
    if (justGenerated && insightsSectionRef.current && groupedAndSortedItems.length > 0) {
      console.log("Scrolling to insights section after generation...");
      insightsSectionRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
      setJustGenerated(false);
    }
  }, [groupedAndSortedItems, justGenerated]);

  // Infinite scroll effect
  const loadMoreInsights = useCallback(() => {
    if (nextPageUrl && !loadingMore) {
      console.log("Triggering loadMoreInsights...");
      fetchUserInsights(nextPageUrl);
    }
  }, [nextPageUrl, loadingMore, fetchUserInsights]);

  useEffect(() => {
    const handleScroll = () => {
      const threshold = 300;
      const nearBottom = window.innerHeight + document.documentElement.scrollTop >= document.documentElement.offsetHeight - threshold;
      if (nearBottom) {
        loadMoreInsights();
      }
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, [loadMoreInsights]);

  // --- Other Handlers ---
  const handleMonitoringSetup = useCallback(
    (e) => {
      e.preventDefault();
      // Called when saving keywords from modal - trigger generation
      handleNewInsight();
    },
    [handleNewInsight]
  );

  const handleConfigChange = useCallback((e) => {
    const { name, value } = e.target;
    setMonitoringConfig((prev) => ({ ...prev, [name]: value }));
  }, []);

  const findHighestRatedInsight = (items) => {
    if (!items || !items.length) return null;

    return items.reduce((highest, current) => {
      // Get score values
      const highestScore = getNumericScore(highest?.details?.potential_score);
      const currentScore = getNumericScore(current?.details?.potential_score);

      // Return the higher scored item
      return currentScore > highestScore ? current : highest;
    }, items[0]);
  };

  // Helper to extract numeric value from potential_score
  const getNumericScore = (scoreString) => {
    if (!scoreString) return 0;

    if (typeof scoreString === "string" && scoreString.includes("/")) {
      const numericPart = parseFloat(scoreString.split("/")[0]);
      return isNaN(numericPart) ? 0 : numericPart;
    }

    if (typeof scoreString === "string") {
      const scoreMap = {
        high: 8,
        medium: 5,
        low: 3,
      };
      return scoreMap[scoreString.toLowerCase()] || 0;
    }

    return 0;
  };

  // --- JSX Return ---
  return (
    // Wrapper div with padding and max-width
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <EnhancedHeader />
      <div className="space-y-8 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pb-12 mt-5">
        {/* Welcome Banner for New Users */}
        {loading && isNewUser === null ? ( // Show loading indicator until user status (isNewUser) is determined
          // Simple loading placeholder
          <div className="h-24 flex flex-col items-center justify-center space-y-2 rounded-xl bg-emerald-50/50 border border-emerald-100 mb-8 text-center">
            {/* Spinner Icon */}
            <Loader2 className="h-8 w-8 text-emerald-600 animate-spin" />

            {/* Loading Text */}
            <span className="text-sm font-medium text-emerald-700">Loading user info...</span>
          </div>
        ) : !isPremium && !userDetails?.accessed_ideation_free_preview ? (
          // STATE 1: User hasn't used free preview yet - show "1 free preview" banner
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, ease: "easeOut" }}
            className="bg-gradient-to-r from-purple-600 to-blue-600 rounded-xl p-6 text-white mb-8 shadow-md"
          >
            <h2 className="text-2xl font-bold mb-2">👋 Welcome!</h2>
            <p className="text-base sm:text-lg text-white/90 mb-4">
              🔍 You have 1 free product idea search available. Enter a topic below to discover validated opportunities! Pro members get
              more searches.
            </p>
            <div className="mt-4">
              <Link
                to="/credits/" // Link to your pricing/upgrade page
                className="inline-block bg-white text-purple-700 font-semibold px-6 py-2 rounded-lg hover:bg-purple-50 transition-colors shadow"
              >
                View Pro Plans
              </Link>
            </div>
          </motion.div>
        ) : !isPremium && userDetails?.accessed_ideation_free_preview && insightCredits <= 0 ? (
          // STATE 2: Free preview used, no subscription, no credits - show upgrade banner
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, ease: "easeOut" }}
            className="bg-green-600 rounded-xl p-6 text-white mb-8 shadow-md" // Use a different style to grab attention
          >
            <h2 className="text-2xl font-bold mb-2">🚀 Unlock More Insights!</h2>
            <p className="text-base sm:text-lg text-white/90 mb-4">
              You've used your free preview search for this feature. Upgrade your plan to continue discovering unlimited market
              opportunities.
            </p>
            <div className="mt-4">
              <Link
                to="/credits/" // Link to your pricing/upgrade page
                className="inline-block bg-white text-black font-semibold px-6 py-2 rounded-lg hover:bg-orange-50 transition-colors shadow"
              >
                Upgrade Plan
              </Link>
            </div>
          </motion.div>
        ) : (
          // // STATE 3: Has subscription or credits - show standard banner
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="relative bg-gradient-to-br from-emerald-700 to-emerald-500 rounded-3xl p-6 sm:p-8 text-white shadow-lg hover:shadow-xl transition-shadow duration-300 overflow-hidden mb-8" // Added mb-8
          >
            {/* Credit Display (Top Right) */}
            <div className="absolute top-4 right-5 sm:top-5 sm:right-6 text-right z-10">
              {/* Check userDetails exists before trying to display credits */}
              {userDetails ? (
                <div className="flex flex-col items-end space-y-1">
                  <div className="px-2 py-1 bg-black/20 rounded-md backdrop-blur-sm">
                    <span className="text-xs text-emerald-100 block">Search Credits Left</span>
                    <span className={`font-semibold text-lg leading-tight block ${insightCredits > 0 ? "text-white" : "text-yellow-300"}`}>
                      {/* Display credits, handle loading state if insightCredits isn't updated yet */}
                      {insightCredits !== null ? insightCredits : "..."}
                    </span>
                  </div>
                  {/* Show Recharge only if credits are zero AND they are NOT on the free plan (where upgrading is the main CTA) */}
                  {isPremium && insightCredits <= 0 && (
                    <Link
                      to="/credits/" // Or specific recharge link
                      className="mt-1 inline-block px-3 py-1 bg-yellow-400 hover:bg-yellow-300 text-yellow-900 text-xs font-bold rounded-md transition-colors shadow-sm"
                      title="Get more credits"
                    >
                      Recharge
                    </Link>
                  )}
                  {/* You could add a 'View Plan' link here too */}
                </div>
              ) : (
                // Placeholder or error if userDetails is somehow null here after initial loading
                <span className="inline-block px-2 py-1 bg-gray-700/50 text-gray-300 text-xs rounded-md">...</span>
              )}
            </div>
            {/* End Credit Info */}

            {/* Header Title & Description */}
            <div className="flex items-center space-x-3 mb-5 pr-20 sm:pr-24">
              {" "}
              {/* Padding right for credits */}
              <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ delay: 0.2, type: "spring", stiffness: 200 }}>
                {" "}
                <Sparkles className="w-8 h-8 text-yellow-300" />{" "}
              </motion.div>
              <h2 className="text-2xl sm:text-3xl font-bold">Real-Time Opportunity Search</h2>
            </div>
            <div className="flex items-start space-x-3 pr-20 sm:pr-24">
              {" "}
              {/* Padding right for credits */}
              <MessageSquare className="w-6 h-6 text-emerald-300 flex-shrink-0 mt-1" />
              <p className="text-emerald-100 text-base sm:text-lg">
                {" "}
                Discover validated business ideas as they emerge from conversations happening now.{" "}
              </p>
            </div>
          </motion.div>
        )}
        {/* --- End Banners / Headers --- */}

        {/* Loading/Generation Overlay Modal */}

        <AnimatePresence>
          {isGeneratingInsights && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            >
              <motion.div
                initial={{ scale: 0.9, opacity: 0, y: 20 }}
                animate={{ scale: 1, opacity: 1, y: 0 }}
                exit={{ scale: 0.95, opacity: 0, y: 10 }}
                transition={{ duration: 0.3, type: "spring", damping: 25 }}
                className="bg-white rounded-2xl p-8 w-full max-w-md shadow-2xl overflow-hidden relative"
              >
                {/* Decorative elements */}
                <div className="absolute -top-24 -right-24 w-48 h-48 bg-gradient-to-br from-emerald-400/20 to-emerald-600/20 rounded-full"></div>
                <div className="absolute -bottom-24 -left-24 w-48 h-48 bg-gradient-to-tr from-blue-400/20 to-blue-600/20 rounded-full"></div>

                {/* Header with icon */}
                <div className="flex flex-col items-center mb-6 relative">
                  <div className="w-16 h-16 rounded-full bg-gradient-to-r from-emerald-500 to-emerald-600 flex items-center justify-center mb-4 shadow-lg shadow-emerald-100">
                    <motion.div
                      animate={{
                        rotate: [0, 360],
                      }}
                      transition={{
                        duration: 4,
                        repeat: Infinity,
                        ease: "linear",
                      }}
                    >
                      <Sparkles className="w-8 h-8 text-white" />
                    </motion.div>
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 text-center">Generating Market Insights</h3>
                  <p className="text-emerald-600 text-sm font-medium mt-1">Powered by AI Market Analysis</p>
                </div>

                {/* Dynamic status message based on progress */}
                <div className="mb-8">
                  <div className="relative">
                    <div className="w-full bg-gray-100 rounded-full h-3 overflow-hidden shadow-inner">
                      <motion.div
                        className="bg-gradient-to-r from-emerald-500 to-emerald-600 h-3 rounded-full relative overflow-hidden"
                        initial={{ width: 0 }}
                        animate={{ width: `${generationProgress}%` }}
                        transition={{ duration: 0.5, ease: "easeOut" }}
                      >
                        {/* Add pulsing effect when progress is in slow sections */}
                        {generationProgress > 60 && generationProgress < 95 && (
                          <motion.div
                            className="absolute inset-0 bg-white/20"
                            animate={{ opacity: [0, 0.5, 0] }}
                            transition={{ duration: 1.5, repeat: Infinity, ease: "easeInOut" }}
                          />
                        )}
                      </motion.div>
                    </div>
                    <motion.div
                      className="mt-2 flex justify-between text-xs text-gray-500"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ delay: 0.5 }}
                    >
                      <span>Starting</span>
                      <span>Processing</span>
                      <span>Finalizing</span>
                    </motion.div>
                  </div>

                  {/* Progress-dependent status messages */}
                  <div className="mt-4 text-center">
                    <AnimatePresence mode="wait">
                      {generationProgress < 30 && (
                        <motion.div
                          key="phase1"
                          initial={{ opacity: 0, y: 5 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: -5 }}
                          className="flex items-center justify-center space-x-2"
                        >
                          <Search className="h-4 w-4 text-emerald-600 animate-pulse" />
                          <span className="text-gray-800 font-medium">Scanning market conversations...</span>
                        </motion.div>
                      )}
                      {generationProgress >= 30 && generationProgress < 60 && (
                        <motion.div
                          key="phase2"
                          initial={{ opacity: 0, y: 5 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: -5 }}
                          className="flex items-center justify-center space-x-2"
                        >
                          <BarChart className="h-4 w-4 text-emerald-600 animate-pulse" />
                          <span className="text-gray-800 font-medium">Analyzing trends and patterns...</span>
                        </motion.div>
                      )}
                      {generationProgress >= 60 && generationProgress < 85 && (
                        <motion.div
                          key="phase3"
                          initial={{ opacity: 0, y: 5 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: -5 }}
                          className="flex items-center justify-center space-x-2"
                        >
                          <Lightbulb className="h-4 w-4 text-emerald-600 animate-pulse" />
                          <span className="text-gray-800 font-medium">Identifying high-value opportunities...</span>
                        </motion.div>
                      )}
                      {generationProgress >= 85 && (
                        <motion.div
                          key="phase4"
                          initial={{ opacity: 0, y: 5 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: -5 }}
                          className="flex items-center justify-center space-x-2"
                        >
                          <CheckCircle className="h-4 w-4 text-emerald-600 animate-pulse" />
                          <span className="text-gray-800 font-medium">Preparing your insights...</span>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>

                  {/* Additional processing indicator for slower sections */}
                  {generationProgress >= 60 && generationProgress < 95 && (
                    <motion.div
                      className="mt-4 flex justify-center"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ delay: 0.5 }}
                    >
                      <motion.div
                        className="px-3 py-1 bg-emerald-50 text-emerald-600 text-xs font-medium rounded-full border border-emerald-200"
                        animate={{ scale: [1, 1.03, 1] }}
                        transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
                      >
                        {generationProgress >= 60 && generationProgress < 75 && "Analyzing market patterns..."}
                        {generationProgress >= 75 && generationProgress < 85 && "Processing large dataset..."}
                        {generationProgress >= 85 && generationProgress < 95 && "Validating opportunities..."}
                      </motion.div>
                    </motion.div>
                  )}
                </div>

                {/* Rotating insights/facts - More frequent changes during long waits */}
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 1 }}
                  className="bg-gradient-to-r from-gray-50 to-gray-100 rounded-xl p-4 mb-6 border border-gray-100"
                >
                  <AnimatePresence mode="wait">
                    <motion.div
                      key={generationProgress < 60 ? Math.floor(generationProgress / 20) : Math.floor(generationProgress / 10)} // More frequent changes during long waits
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -10 }}
                      transition={{ duration: 0.5 }}
                      className="flex items-start space-x-3"
                    >
                      <div className="flex-shrink-0 mt-0.5">
                        {generationProgress < 20 && <TrendingUp className="h-5 w-5 text-blue-600" />}
                        {generationProgress >= 20 && generationProgress < 40 && <Users className="h-5 w-5 text-purple-600" />}
                        {generationProgress >= 40 && generationProgress < 60 && <Target className="h-5 w-5 text-red-600" />}
                        {generationProgress >= 60 && generationProgress < 70 && <Lightbulb className="h-5 w-5 text-amber-600" />}
                        {generationProgress >= 70 && generationProgress < 80 && <BarChart className="h-5 w-5 text-blue-600" />}
                        {generationProgress >= 80 && generationProgress < 90 && <Compass className="h-5 w-5 text-indigo-600" />}
                        {generationProgress >= 90 && <Sparkles className="h-5 w-5 text-emerald-600" />}
                      </div>
                      <div>
                        <p className="text-sm text-gray-700 leading-relaxed">
                          {generationProgress < 20 &&
                            "Did you know? Companies that identify market opportunities early have a 3.5x higher success rate than those who follow market trends."}
                          {generationProgress >= 20 &&
                            generationProgress < 40 &&
                            "Our AI analyzes thousands of data points to identify user personas with the highest potential ROI for your specific market."}
                          {generationProgress >= 40 &&
                            generationProgress < 60 &&
                            "We're identifying gaps between what users want and what existing solutions provide—the perfect space for innovation."}
                          {generationProgress >= 60 &&
                            generationProgress < 70 &&
                            "The best business opportunities often come from solving problems that people have accepted as 'normal' or unavoidable."}
                          {generationProgress >= 70 &&
                            generationProgress < 80 &&
                            "We're examining conversation patterns to identify recurring pain points that have clear monetization potential."}
                          {generationProgress >= 80 &&
                            generationProgress < 90 &&
                            "Great ideas often come from combining multiple user needs into one elegant solution. We're mapping these connections now."}
                          {generationProgress >= 90 &&
                            "The final stage involves validating our findings against market trends to ensure you receive only the highest quality opportunities."}
                        </p>
                      </div>
                    </motion.div>
                  </AnimatePresence>
                </motion.div>

                {/* Completion estimate */}
                <div className="text-center">
                  <div className="flex items-center justify-center gap-2 text-sm text-gray-500">
                    <Clock className="h-4 w-4" />
                    <span>
                      {generationProgress < 50
                        ? "Estimated completion in 2-3 minutes"
                        : generationProgress < 80
                        ? "Almost there! Just a bit longer..."
                        : generationProgress < 95
                        ? "In final processing stage..."
                        : "Finalizing your results now!"}
                    </span>
                  </div>
                  <p className="text-xs text-gray-400 mt-2">
                    Our AI is working hard to deliver exceptional results. Please don't close this window.
                  </p>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Keywords Monitoring Section */}
        <div className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden">
          <div className="p-6 sm:p-8">
            <h2 className="text-xl sm:text-2xl font-semibold text-gray-900 mb-1">What Market Are You Curious About?</h2>
            <p className="text-sm text-gray-500 mb-5">Enter a topic, industry, or product area below.</p>
            <div className="mb-6">
              <label htmlFor="keyword-button" className="block text-sm font-medium text-gray-700 mb-2 sr-only">
                Topic or Industry Focus
              </label>
              <button
                id="keyword-button"
                type="button"
                onClick={() => setShowKeywordModal(true)}
                className="w-full flex items-center justify-between bg-gray-50 border border-gray-200 rounded-xl py-3 px-4 text-left text-gray-700 hover:border-emerald-300 hover:bg-emerald-50 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:ring-offset-2 transition-all duration-200 group"
                aria-label="Edit topic focus"
              >
                <span className={`truncate ${!monitoringConfig.keywords ? "text-gray-400" : ""}`}>
                  {monitoringConfig.keywords || "Enter a single topic or industry"}
                </span>
                <Pencil size={18} className="text-gray-400 group-hover:text-emerald-600 ml-2 flex-shrink-0" />
              </button>
            </div>
            <div className="bg-emerald-50/60 border border-emerald-100 rounded-xl p-4 mb-6 flex items-start space-x-3">
              <Info size={20} className="text-emerald-500 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm text-emerald-800 font-medium mb-2">Examples:</p>
                <div className="flex flex-wrap gap-2">
                  {suggestedKeywords.map((keyword) => (
                    <span
                      key={keyword}
                      className="bg-emerald-100 text-emerald-800 px-2.5 py-0.5 rounded-full text-xs font-medium cursor-default"
                    >
                      {keyword}
                    </span>
                  ))}
                </div>
              </div>
            </div>
            {/* Action Button */}
            <button
              type="button"
              className={`w-full flex items-center justify-center bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700 text-white text-base font-medium py-3.5 px-6 rounded-xl transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:ring-offset-2 shadow-md disabled:opacity-60 disabled:cursor-not-allowed ${
                isGeneratingInsights ? "cursor-wait" : ""
              }`}
              onClick={handleNewInsight}
              // disabled={isGeneratingInsights || !monitoringConfig.keywords || (insightCredits <= 0 && isNewUser === false)}
              // title={
              //   isGeneratingInsights
              //     ? "Generation in progress..."
              //     : !monitoringConfig.keywords
              //     ? "Please enter a topic first"
              //     : insightCredits <= 0 && isNewUser === false
              //     ? "Requires Insight Credits"
              //     : "Find Opportunities"
              // }

              disabled={
                userDetails === null || // Check if userDetails are loaded first
                isGeneratingInsights ||
                !monitoringConfig.keywords ||
                // --- Block ONLY IF: Not Premium AND Preview Used AND No Credits ---
                (!isPremium && isNewUser === false && insightCredits <= 0)
              }
              title={
                userDetails === null // Check loading first
                  ? "Loading user status..."
                  : isGeneratingInsights
                  ? "Generation in progress..."
                  : !monitoringConfig.keywords
                  ? "Please enter a topic first"
                  : // --- Title Check Matches Disabled Logic ---
                  !isPremium && isNewUser === false && insightCredits <= 0
                  ? "Free preview used or no credits. Upgrade required." // Blocking reason
                  : // Default action text if allowed
                    "Find Opportunities"
              }
            >
              <Sparkles size={18} className={`mr-2 ${isGeneratingInsights ? "animate-spin" : ""}`} />
              {isGeneratingInsights ? "Generating Opportunities..." : "Find Opportunities"}
            </button>
          </div>
        </div>

        {/* --- Results Display Section (Using Grouped Data) --- */}
        <div className="mt-8 sm:mt-12">
          {loading && rawDisplayItems.length === 0 ? ( // Show loading ONLY on initial load when raw items are empty
            <div className="flex flex-col justify-center items-center py-12">
              <Loader2 className="h-10 w-10 text-green-600 animate-spin mb-3" />
              <span className="text-gray-600">Loading results...</span>
            </div>
          ) : error ? ( // Error state
            <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-6 shadow-sm">
              <div className="flex items-center">
                <AlertCircle className="h-5 w-5 text-red-500 mr-3 flex-shrink-0" />
                <p className="text-red-700 text-sm font-medium">{error}</p>
              </div>
            </div>
          ) : groupedAndSortedItems && groupedAndSortedItems.length > 0 ? (
            // *** Main display logic iterating over groups ***
            <div ref={insightsSectionRef} className="space-y-12">
              {" "}
              {/* Attach ref here if needed */}
              {groupedAndSortedItems.map(([keyString, group], groupIndex) => {
                // Get keywords for header
                let keywords = [];
                try {
                  keywords = JSON.parse(keyString);
                } catch (e) {
                  keywords = ["unknown_topic"];
                }

                // Create unique key for group
                const groupKey = keyString + "-" + (group.latestDate?.toISOString() || groupIndex);

                // Filter persona cards and insight cards
                const personaCards = group.items.filter((item) => item.details?.type === "personas");
                const insightCards = group.items.filter((item) => item.details?.type !== "personas");

                // Find the highest rated insight in this group
                const highestRatedInsight = findHighestRatedInsight(insightCards);
                const highestRatedInsightId = highestRatedInsight?.id;

                // Check user status
                const hasAccess = isPremium || insightCredits > 0;

                return (
                  <div key={groupKey} className="border-t border-gray-200 pt-8 first:border-t-0 first:pt-0">
                    {/* Group Header */}
                    <div className="mb-6 px-1">
                      <span className="text-sm font-semibold text-gray-500 uppercase tracking-wider">Results for:</span>
                      <div className="flex flex-wrap gap-2 mt-1">
                        {keywords.length > 0 && keywords[0] !== "unknown_topic" ? (
                          keywords.map((kw) => (
                            <span key={kw} className="bg-emerald-100 text-emerald-800 px-3 py-1 rounded-full text-sm font-medium">
                              {kw}
                            </span>
                          ))
                        ) : (
                          <span className="text-gray-400 italic"> (Keywords not specified)</span>
                        )}
                      </div>
                    </div>

                    {/* Display items */}
                    <div className="space-y-8">
                      {/* Show only the first persona card - with premium styling */}
                      {personaCards.length > 0 && (
                        <PersonasAggregateCard details={personaCards[0].details} isPremium={isPremium} insightCredits={insightCredits} />
                      )}

                      {/* Insights Section */}
                      {insightCards.length > 0 && (
                        <div className="space-y-8">
                          {/* Free Preview Banner - Shown when not premium */}
                          {!hasAccess && (
                            <div className="bg-gradient-to-r from-amber-50 to-orange-50 rounded-xl border border-amber-100 p-5 mb-6 shadow-sm">
                              <div className="flex items-center">
                                <div className="flex-shrink-0 bg-amber-100 rounded-full p-3 mr-4 shadow-sm">
                                  <Sparkles className="h-6 w-6 text-amber-600" />
                                </div>
                                <div>
                                  <h4 className="font-semibold text-amber-800 text-lg">Free Preview</h4>
                                  <p className="text-amber-700 mt-1">
                                    You're seeing a preview of your first opportunity for free.
                                    <span className="font-semibold">
                                      {" "}
                                      {insightCards.length > 1 ? `${insightCards.length - 1} more insights` : "Complete analysis"}
                                    </span>{" "}
                                    available with upgrade.
                                  </p>
                                </div>
                              </div>
                            </div>
                          )}

                          {/* First show the highest rated insight with special styling */}
                          {highestRatedInsight && (
                            <div className="space-y-4">
                              <InsightCard
                                insight={highestRatedInsight}
                                index={0}
                                bookmarkedPainPoints={bookmarkedPainPoints}
                                handleToggleBookmark={() => handleToggleBookmark(highestRatedInsight.id)}
                                isPremium={isPremium}
                                insightCredits={insightCredits}
                                isHighestRated={true}
                                isExpanded={expandedCards.has(highestRatedInsight.id)} // Pass expanded state
                                onToggleExpand={() => toggleExpand(highestRatedInsight.id)} // Pass toggle function
                              />
                            </div>
                          )}

                          {/* Premium Features Banner - Show between top insight and others */}
                          {!hasAccess && insightCards.length > 1 && (
                            <div className="bg-gradient-to-r from-emerald-50 to-blue-50 rounded-xl p-6 mb-6 border border-emerald-100 shadow-sm">
                              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-5">
                                <div className="flex items-center">
                                  <div className="relative">
                                    <div className="bg-gradient-to-br from-emerald-400 to-emerald-500 rounded-lg p-3 mr-4 shadow-md">
                                      <Lock className="h-6 w-6 text-white" />
                                    </div>
                                  </div>
                                  <div>
                                    <h4 className="font-semibold text-emerald-800 text-lg">Unlock Premium Features</h4>
                                    <p className="text-emerald-700 mt-1">
                                      <span className="font-semibold">
                                        {insightCards.length - 1} more {insightCards.length - 2 === 0 ? "insight" : "insights"}
                                      </span>{" "}
                                      available with strategic analysis and implementation guides.
                                    </p>
                                  </div>
                                </div>
                                <Link
                                  to="/credits/"
                                  className="bg-gradient-to-r from-emerald-500 to-emerald-600 hover:brightness-105 text-white px-6 py-3 rounded-xl text-base font-medium transition-all duration-300 shadow md:min-w-40 text-center flex items-center justify-center group"
                                >
                                  <Zap className="h-5 w-5 mr-2 group-hover:scale-110 transition-transform duration-200" />
                                  Upgrade Now
                                  <ChevronRight
                                    className="ml-2 opacity-0 group-hover:opacity-100 group-hover:translate-x-1 transition-all duration-300"
                                    size={16}
                                  />
                                </Link>
                              </div>
                            </div>
                          )}

                          {/* Remaining insight cards - blurred for non-premium users */}
                          {insightCards
                            .filter((item) => item.id !== highestRatedInsightId)
                            .map((item, itemIndex) => {
                              const itemKey = item.id || `insight-${groupKey}-${itemIndex + 1}`;

                              // If premium or has credits, show normal card
                              if (hasAccess) {
                                return (
                                  <div key={itemKey} className="space-y-4">
                                    <InsightCard
                                      insight={item}
                                      index={itemIndex + 1}
                                      bookmarkedPainPoints={bookmarkedPainPoints}
                                      handleToggleBookmark={() => handleToggleBookmark(item.id)}
                                      isPremium={isPremium}
                                      insightCredits={insightCredits}
                                      isExpanded={expandedCards.has(item.id)} // Pass expanded state
                                      onToggleExpand={() => toggleExpand(item.id)} // Pass toggle function
                                    />
                                  </div>
                                );
                              }

                              // Otherwise show blurred card
                              return (
                                <div key={itemKey}>
                                  <InsightCard
                                    insight={item}
                                    index={itemIndex + 1}
                                    bookmarkedPainPoints={new Set()}
                                    handleToggleBookmark={() => {}}
                                    isPremium={false}
                                    insightCredits={0}
                                    isBlurred={true}
                                    isExpanded={expandedCards.has(item.id)} // Pass expanded state
                                    onToggleExpand={() => toggleExpand(item.id)} // Pass toggle function
                                  />
                                </div>
                              );
                            })}

                          {/* Bottom CTA Banner - final reminder to upgrade */}
                          {!hasAccess && insightCards.length > 1 && (
                            <div className="mt-8 bg-white border border-gray-200 rounded-xl p-6 shadow-sm text-center">
                              <div className="w-16 h-16 mx-auto bg-gradient-to-br from-blue-400 to-emerald-400 rounded-full flex items-center justify-center shadow-md mb-4">
                                <Zap className="h-8 w-8 text-white" />
                              </div>
                              <h3 className="text-xl font-bold text-gray-800 mb-2">Ready to Unlock All {insightCards.length} Insights?</h3>
                              <p className="text-gray-600 mb-6 max-w-md mx-auto">
                                Get complete analysis, strategic guidance, and implementation plans for all opportunities.
                              </p>
                              <Link
                                to="/credits/"
                                className="inline-flex items-center bg-gradient-to-r from-blue-500 to-emerald-500 hover:brightness-105 text-white px-8 py-3 rounded-xl font-medium transition-all duration-300 shadow-md group"
                              >
                                Upgrade to Premium
                                <ChevronRight className="ml-2 group-hover:translate-x-1 transition-transform duration-300" size={18} />
                              </Link>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div> // End space-y-12 for groups
          ) : (
            // View when no items are available after loading/no error
            <div className="text-center py-12 bg-white rounded-xl shadow-sm border border-gray-100 mt-8">
              <div className="rounded-full bg-gray-100 p-4 inline-block mx-auto mb-4">
                <Search className="h-8 w-8 text-gray-400" />
              </div>
              <h3 className="text-lg sm:text-xl font-medium text-gray-900 mb-2">No results to display yet</h3>
              <p className="text-gray-500 text-sm mb-5 max-w-md mx-auto">Enter a topic above and click "Find Opportunities".</p>
            </div>
          )}
          {/* Loading More Indicator */}
          {loadingMore && (
            <div className="text-center py-4 mt-8">
              {" "}
              <Loader2 className="animate-spin mx-auto text-green-600" size={24} />{" "}
              <p className="text-gray-600 mt-2 text-sm">Loading more results...</p>{" "}
            </div>
          )}
          {/* End of results message */}
          {!loadingMore && !nextPageUrl && rawDisplayItems.length > 0 && (
            <div className="text-center py-4 mt-8 text-gray-500 text-sm">End of results.</div>
          )}
        </div>
        {/* --- End Results Display Section --- */}
        {/* --- End Results Display Section --- */}

        {/* Keyword Modal */}
        {showKeywordModal && (
          <AnimatePresence>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black bg-opacity-60 z-40 flex items-center justify-center p-4"
            >
              <motion.div
                initial={{ scale: 0.95, y: -20 }}
                animate={{ scale: 1, y: 0 }}
                exit={{ scale: 0.95, y: -20, opacity: 0 }}
                transition={{ duration: 0.2, ease: "easeOut" }}
                className="bg-white rounded-xl p-6 w-full max-w-md shadow-xl border border-gray-200"
              >
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-medium leading-6 text-gray-900">Set Topic/Industry Focus</h3>
                  <button onClick={() => setShowKeywordModal(false)} className="text-gray-400 hover:text-gray-600">
                    <X size={20} />
                  </button>
                </div>
                <p className="text-sm text-gray-500 mb-4">
                  Enter a single, specific topic like "SaaS marketing", "sustainable packaging", or "AI video tools".
                </p>
                <input
                  type="text"
                  value={monitoringConfig.keywords}
                  onChange={handleConfigChange}
                  name="keywords"
                  className="w-full px-3 py-2 text-gray-700 border border-gray-300 rounded-lg focus:outline-none focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 mb-6"
                  placeholder="e.g., AI marketing tools"
                  aria-label="Topic or Industry"
                />
                <div className="flex justify-end space-x-3">
                  <button
                    type="button"
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 ..."
                    onClick={() => setShowKeywordModal(false)}
                  >
                    {" "}
                    Cancel{" "}
                  </button>
                  <button
                    type="button"
                    className="px-5 py-2 text-sm font-medium text-white bg-emerald-600 rounded-lg hover:bg-emerald-700 ..."
                    onClick={() => {
                      if (monitoringConfig.keywords.trim()) {
                        handleMonitoringSetup({ preventDefault: () => {} });
                      }
                      setShowKeywordModal(false);
                    }}
                    disabled={!monitoringConfig.keywords.trim()}
                    title={!monitoringConfig.keywords.trim() ? "Enter a topic first" : "Save and Find Opportunities"}
                  >
                    {" "}
                    Save & Find Opportunities{" "}
                  </button>
                </div>
              </motion.div>
            </motion.div>
          </AnimatePresence>
        )}

        {/* Upgrade Modal */}
        <UpgradeModal show={showUpgradeModal} onClose={() => setShowUpgradeModal(false)} navigate={navigate} />
      </div>
    </div>
    // End main wrapper
  );
};

export default InsightsTab;

// import React, { useState } from "react";
// import { Search, ArrowRight, Twitter, MessageSquare, BarChart2, RefreshCw, ThumbsUp, ThumbsDown, Zap } from "lucide-react";

// const IdeasValidatorApp = () => {
//   const [currentView, setCurrentView] = useState("input"); // 'input', 'results', 'report'
//   const [idea, setIdea] = useState("");
//   const [isLoading, setIsLoading] = useState(false);
//   const [category, setCategory] = useState("");

//   const handleSubmit = (e) => {
//     e.preventDefault();
//     if (!idea.trim()) return;

//     setIsLoading(true);
//     // In a real app, this would be an API call
//     setTimeout(() => {
//       setCurrentView("results");
//       setIsLoading(false);
//     }, 1500);
//   };

//   const handleAnalyze = () => {
//     setIsLoading(true);
//     // In a real app, this would be an API call
//     setTimeout(() => {
//       setCurrentView("report");
//       setIsLoading(false);
//     }, 2000);
//   };

//   const handleReset = () => {
//     setCurrentView("input");
//     setIdea("");
//   };

//   // Sample data for demonstration
//   const samplePosts = [
//     {
//       id: 1,
//       platform: "twitter",
//       author: "productmanager_io",
//       content:
//         "Spent hours searching for feedback on my SaaS idea. If only there was a tool that could automatically find relevant social discussions and analyze them!",
//       engagement: 89,
//       sentiment: "negative",
//       date: "2025-04-10",
//     },
//     {
//       id: 2,
//       platform: "reddit",
//       author: "startup_founder",
//       subreddit: "r/Entrepreneur",
//       content:
//         "How do you guys validate your business ideas? I've been manually searching through Twitter and Reddit, but it takes so much time.",
//       engagement: 42,
//       sentiment: "neutral",
//       date: "2025-04-15",
//     },
//     {
//       id: 3,
//       platform: "twitter",
//       author: "saas_guru",
//       content:
//         "The biggest mistake founders make is building without validation. Talk to users, check social media, see if people are already discussing the problem.",
//       engagement: 216,
//       sentiment: "positive",
//       date: "2025-04-12",
//     },
//     {
//       id: 4,
//       platform: "reddit",
//       author: "indie_hacker",
//       subreddit: "r/startups",
//       content:
//         "Anyone else sick of using 5 different tools just to validate an idea? I want ONE place that shows me if people actually want my product.",
//       engagement: 73,
//       sentiment: "negative",
//       date: "2025-04-08",
//     },
//   ];

//   // Input View
//   if (currentView === "input") {
//     return (
//       <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white flex flex-col items-center">
//         <header className="w-full py-6 px-4 flex justify-center">
//           <div className="flex items-center space-x-2">
//             <Zap className="text-blue-500" size={28} />
//             <h1 className="text-2xl font-bold text-gray-800">IdeaValidator</h1>
//           </div>
//         </header>

//         <main className="flex-1 w-full max-w-3xl px-4 flex flex-col items-center justify-center pb-20">
//           <div className="text-center mb-8">
//             <h2 className="text-3xl font-bold text-gray-800 mb-3">Validate Your Business Idea</h2>
//             <p className="text-gray-600 max-w-lg mx-auto">
//               See what people are saying about your idea across social media and get a comprehensive business analysis.
//             </p>
//           </div>

//           <form onSubmit={handleSubmit} className="w-full space-y-4">
//             <div className="bg-white rounded-xl shadow-xl p-6 w-full">
//               <label className="block text-sm font-medium text-gray-700 mb-2">Describe your business idea or feature</label>
//               <textarea
//                 value={idea}
//                 onChange={(e) => setIdea(e.target.value)}
//                 className="w-full p-4 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 min-h-32"
//                 placeholder="Example: A tool that helps entrepreneurs validate business ideas by analyzing social media discussions and generating a comprehensive report with market insights."
//                 required
//               />

//               <div className="mt-4">
//                 <label className="block text-sm font-medium text-gray-700 mb-2">Category (optional)</label>
//                 <select
//                   value={category}
//                   onChange={(e) => setCategory(e.target.value)}
//                   className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
//                 >
//                   <option value="">Select a category</option>
//                   <option value="saas">SaaS / Software</option>
//                   <option value="ecommerce">E-commerce</option>
//                   <option value="mobile">Mobile App</option>
//                   <option value="marketplace">Marketplace</option>
//                   <option value="content">Content / Media</option>
//                   <option value="other">Other</option>
//                 </select>
//               </div>
//             </div>

//             <button
//               type="submit"
//               disabled={isLoading}
//               className="w-full bg-blue-600 hover:bg-blue-700 text-white font-bold py-4 px-6 rounded-lg shadow-lg flex items-center justify-center transition-colors"
//             >
//               {isLoading ? (
//                 <>
//                   <RefreshCw className="animate-spin mr-2" size={20} />
//                   Searching social media...
//                 </>
//               ) : (
//                 <>
//                   <Search className="mr-2" size={20} />
//                   Find Social Media Discussions
//                 </>
//               )}
//             </button>
//           </form>

//           <div className="mt-8 flex items-center space-x-4 text-gray-500 text-sm">
//             <div className="flex items-center">
//               <Twitter className="mr-1" size={16} />
//               <span>Twitter data</span>
//             </div>
//             <div className="flex items-center">
//               <MessageSquare className="mr-1" size={16} />
//               <span>Reddit discussions</span>
//             </div>
//             <div className="flex items-center">
//               <BarChart2 className="mr-1" size={16} />
//               <span>AI Analysis</span>
//             </div>
//           </div>
//         </main>
//       </div>
//     );
//   }

//   // Results View
//   if (currentView === "results") {
//     return (
//       <div className="min-h-screen bg-white">
//         <header className="bg-white border-b border-gray-200 sticky top-0 z-10">
//           <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
//             <div className="flex items-center space-x-2">
//               <Zap className="text-blue-500" size={24} />
//               <h1 className="text-xl font-bold text-gray-800">IdeaValidator</h1>
//             </div>
//             <button onClick={handleReset} className="text-gray-600 hover:text-gray-800 text-sm font-medium">
//               New Search
//             </button>
//           </div>
//         </header>

//         <main className="max-w-6xl mx-auto px-4 py-6">
//           <div className="mb-6">
//             <h2 className="text-2xl font-bold text-gray-800 mb-2">Social Media Results</h2>
//             <p className="text-gray-600">
//               We found <span className="font-semibold">28 relevant discussions</span> about ideas similar to:
//               <span className="italic ml-1">{idea.length > 100 ? idea.substring(0, 100) + "..." : idea}</span>
//             </p>
//           </div>

//           <div className="flex flex-wrap gap-4 mb-6">
//             <button className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm font-medium">All Platforms</button>
//             <button className="bg-gray-100 text-gray-800 px-3 py-1 rounded-full text-sm font-medium flex items-center">
//               <Twitter size={14} className="mr-1" />
//               Twitter
//             </button>
//             <button className="bg-gray-100 text-gray-800 px-3 py-1 rounded-full text-sm font-medium flex items-center">
//               <MessageSquare size={14} className="mr-1" />
//               Reddit
//             </button>
//             <button className="bg-gray-100 text-gray-800 px-3 py-1 rounded-full text-sm font-medium flex items-center">
//               <ThumbsUp size={14} className="mr-1" />
//               Positive
//             </button>
//             <button className="bg-gray-100 text-gray-800 px-3 py-1 rounded-full text-sm font-medium flex items-center">
//               <ThumbsDown size={14} className="mr-1" />
//               Negative
//             </button>
//           </div>

//           <div className="grid gap-4">
//             {samplePosts.map((post) => (
//               <div key={post.id} className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
//                 <div className="flex items-center mb-2">
//                   {post.platform === "twitter" ? (
//                     <Twitter size={16} className="text-blue-400 mr-2" />
//                   ) : (
//                     <MessageSquare size={16} className="text-orange-500 mr-2" />
//                   )}
//                   <span className="font-medium">{post.platform === "twitter" ? `@${post.author}` : post.author}</span>
//                   {post.platform === "reddit" && <span className="ml-2 text-gray-500 text-sm">{post.subreddit}</span>}
//                   <span className="ml-auto text-gray-500 text-sm">{post.date}</span>
//                 </div>
//                 <p className="text-gray-800 mb-3">{post.content}</p>
//                 <div className="flex items-center text-sm">
//                   <span className="text-gray-500">
//                     {post.platform === "twitter" ? `${post.engagement} likes` : `${post.engagement} upvotes`}
//                   </span>
//                   <span
//                     className={`ml-auto px-2 py-1 rounded-full text-xs ${
//                       post.sentiment === "positive"
//                         ? "bg-green-100 text-green-800"
//                         : post.sentiment === "negative"
//                         ? "bg-red-100 text-red-800"
//                         : "bg-gray-100 text-gray-800"
//                     }`}
//                   >
//                     {post.sentiment.charAt(0).toUpperCase() + post.sentiment.slice(1)}
//                   </span>
//                 </div>
//               </div>
//             ))}
//           </div>

//           <div className="mt-8 mb-10 text-center">
//             <p className="text-gray-600 mb-4">Ready to analyze all 28 posts and get a comprehensive business report?</p>
//             <button
//               onClick={handleAnalyze}
//               disabled={isLoading}
//               className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-4 px-10 rounded-lg shadow-lg inline-flex items-center justify-center transition-colors text-lg"
//             >
//               {isLoading ? (
//                 <>
//                   <RefreshCw className="animate-spin mr-2" size={20} />
//                   Analyzing content...
//                 </>
//               ) : (
//                 <>
//                   <BarChart2 className="mr-2" size={20} />
//                   ANALYZE & GENERATE REPORT
//                 </>
//               )}
//             </button>
//           </div>
//         </main>
//       </div>
//     );
//   }

//   // Report View
//   if (currentView === "report") {
//     return (
//       <div className="min-h-screen bg-white">
//         <header className="bg-white border-b border-gray-200 sticky top-0 z-10">
//           <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
//             <div className="flex items-center space-x-2">
//               <Zap className="text-blue-500" size={24} />
//               <h1 className="text-xl font-bold text-gray-800">IdeaValidator</h1>
//             </div>
//             <div className="flex items-center space-x-4">
//               <button onClick={() => setCurrentView("results")} className="text-gray-600 hover:text-gray-800 text-sm font-medium">
//                 Back to Results
//               </button>
//               <button onClick={handleReset} className="text-gray-600 hover:text-gray-800 text-sm font-medium">
//                 New Search
//               </button>
//             </div>
//           </div>
//         </header>

//         <main className="max-w-4xl mx-auto px-4 py-6">
//           <div className="mb-6">
//             <h1 className="text-3xl font-bold text-gray-800 mb-2">Business Idea Assessment Report</h1>
//             <p className="text-gray-600">Analysis based on 28 relevant social media discussions</p>
//           </div>

//           <div className="bg-blue-50 border border-blue-200 rounded-xl p-6 mb-8">
//             <div className="flex items-center justify-between mb-4">
//               <h2 className="text-xl font-bold text-gray-800">Executive Summary</h2>
//               <div className="bg-blue-600 text-white font-bold rounded-full w-16 h-16 flex items-center justify-center text-xl">
//                 82<span className="text-xs">/100</span>
//               </div>
//             </div>
//             <p className="text-gray-700">
//               Your idea validation tool shows strong market potential with clear demand from entrepreneurs and product managers. Social
//               media analysis reveals significant pain points around manual validation processes that your solution directly addresses.
//             </p>
//           </div>

//           <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
//             <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
//               <h2 className="text-lg font-bold text-gray-800 mb-4">Sentiment Analysis</h2>
//               <div className="flex justify-between mb-4">
//                 <div className="text-center">
//                   <div className="text-green-500 font-bold text-xl">42%</div>
//                   <div className="text-sm text-gray-600">Positive</div>
//                 </div>
//                 <div className="text-center">
//                   <div className="text-red-500 font-bold text-xl">35%</div>
//                   <div className="text-sm text-gray-600">Negative</div>
//                 </div>
//                 <div className="text-center">
//                   <div className="text-gray-500 font-bold text-xl">23%</div>
//                   <div className="text-sm text-gray-600">Neutral</div>
//                 </div>
//               </div>
//               <div className="w-full bg-gray-200 rounded-full h-2.5 mb-4">
//                 <div className="bg-green-500 h-2.5 rounded-full" style={{ width: "42%" }}></div>
//               </div>
//               <p className="text-sm text-gray-600">
//                 Most negative sentiments relate to current validation processes, not your solution concept.
//               </p>
//             </div>

//             <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
//               <h2 className="text-lg font-bold text-gray-800 mb-4">Optimized Idea</h2>
//               <div className="mb-4">
//                 <div className="text-sm text-gray-500 mb-1">Original</div>
//                 <div className="text-gray-800 bg-gray-50 p-2 rounded text-sm">An idea validation tool for entrepreneurs</div>
//               </div>
//               <div>
//                 <div className="text-sm text-gray-500 mb-1">Refined</div>
//                 <div className="text-gray-800 bg-blue-50 border border-blue-100 p-2 rounded text-sm font-medium">
//                   An all-in-one idea validation platform that automatically analyzes social media discussions and generates comprehensive
//                   business reports with competitive analysis and market sizing.
//                 </div>
//               </div>
//             </div>
//           </div>

//           <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm mb-8">
//             <h2 className="text-lg font-bold text-gray-800 mb-4">Competitive Landscape</h2>
//             <div className="space-y-4">
//               <div className="border-b border-gray-100 pb-4">
//                 <div className="flex justify-between items-start">
//                   <div>
//                     <h3 className="font-medium text-gray-800">ProductHunt Validate</h3>
//                     <p className="text-sm text-gray-600">Community-focused validation</p>
//                   </div>
//                   <div className="text-sm bg-yellow-100 text-yellow-800 px-2 py-1 rounded">Indirect Competitor</div>
//                 </div>
//                 <div className="mt-2 text-sm">
//                   <div className="flex">
//                     <span className="text-gray-500 w-24">Strengths:</span>
//                     <span className="text-gray-700">Large user community, simple interface</span>
//                   </div>
//                   <div className="flex">
//                     <span className="text-gray-500 w-24">Weaknesses:</span>
//                     <span className="text-gray-700">Limited data sources, no comprehensive analysis</span>
//                   </div>
//                   <div className="flex">
//                     <span className="text-gray-500 w-24">Pricing:</span>
//                     <span className="text-gray-700">Free with ProductHunt account</span>
//                   </div>
//                 </div>
//               </div>

//               <div className="border-b border-gray-100 pb-4">
//                 <div className="flex justify-between items-start">
//                   <div>
//                     <h3 className="font-medium text-gray-800">SurveyMonkey Audience</h3>
//                     <p className="text-sm text-gray-600">Survey-based validation</p>
//                   </div>
//                   <div className="text-sm bg-yellow-100 text-yellow-800 px-2 py-1 rounded">Indirect Competitor</div>
//                 </div>
//                 <div className="mt-2 text-sm">
//                   <div className="flex">
//                     <span className="text-gray-500 w-24">Strengths:</span>
//                     <span className="text-gray-700">Targeted audience selection, structured feedback</span>
//                   </div>
//                   <div className="flex">
//                     <span className="text-gray-500 w-24">Weaknesses:</span>
//                     <span className="text-gray-700">Expensive, no social listening, artificial environment</span>
//                   </div>
//                   <div className="flex">
//                     <span className="text-gray-500 w-24">Pricing:</span>
//                     <span className="text-gray-700">$1+ per response</span>
//                   </div>
//                 </div>
//               </div>

//               <p className="text-sm text-gray-600 mt-4">
//                 <span className="font-medium">Market Gap:</span> There is a clear opportunity for an automated solution that combines social
//                 listening with AI analysis specifically for idea validation.
//               </p>
//             </div>
//           </div>

//           <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
//             <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm md:col-span-2">
//               <h2 className="text-lg font-bold text-gray-800 mb-4">Target Personas</h2>
//               <div className="space-y-6">
//                 <div className="border-b border-gray-100 pb-4">
//                   <h3 className="font-medium text-gray-800">Early-Stage Founder Finn</h3>
//                   <div className="mt-2 text-sm">
//                     <div className="flex">
//                       <span className="text-gray-500 w-24">Demographics:</span>
//                       <span className="text-gray-700">25-35, technical background, bootstrapping</span>
//                     </div>
//                     <div className="flex">
//                       <span className="text-gray-500 w-24">Pain Points:</span>
//                       <span className="text-gray-700">Limited budget, needs quick validation before building</span>
//                     </div>
//                     <div className="flex">
//                       <span className="text-gray-500 w-24">Quote:</span>
//                       <span className="text-gray-700 italic">"I can't afford to build something nobody wants"</span>
//                     </div>
//                   </div>
//                 </div>

//                 <div>
//                   <h3 className="font-medium text-gray-800">Product Manager Paula</h3>
//                   <div className="mt-2 text-sm">
//                     <div className="flex">
//                       <span className="text-gray-500 w-24">Demographics:</span>
//                       <span className="text-gray-700">30-45, mid-size company, data-driven</span>
//                     </div>
//                     <div className="flex">
//                       <span className="text-gray-500 w-24">Pain Points:</span>
//                       <span className="text-gray-700">Needs evidence to pitch new features internally</span>
//                     </div>
//                     <div className="flex">
//                       <span className="text-gray-500 w-24">Quote:</span>
//                       <span className="text-gray-700 italic">"My team won't approve resources without proof"</span>
//                     </div>
//                   </div>
//                 </div>
//               </div>
//             </div>

//             <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
//               <h2 className="text-lg font-bold text-gray-800 mb-4">Pricing Strategy</h2>
//               <div className="text-center mb-4">
//                 <div className="text-3xl font-bold text-blue-600">$29</div>
//                 <div className="text-gray-600">per month</div>
//               </div>
//               <div className="space-y-2 text-sm">
//                 <div className="flex justify-between">
//                   <span>Entry tier:</span>
//                   <span className="font-medium">$19/mo</span>
//                 </div>
//                 <div className="flex justify-between">
//                   <span>Premium tier:</span>
//                   <span className="font-medium">$49/mo</span>
//                 </div>
//                 <div className="flex justify-between">
//                   <span>Pay-per-report:</span>
//                   <span className="font-medium">$39</span>
//                 </div>
//               </div>
//               <p className="text-sm text-gray-600 mt-4">
//                 Recommendation based on competitor pricing and perceived value from social media sentiment.
//               </p>
//             </div>
//           </div>

//           <div className="bg-blue-50 border border-blue-200 rounded-xl p-6 mb-4">
//             <h2 className="text-lg font-bold text-gray-800 mb-4">Conclusion & Recommendations</h2>
//             <p className="text-gray-700 mb-4">
//               Your idea validation tool shows strong market potential with a clear need expressed across social media platforms. There are
//               no direct competitors offering the same comprehensive solution.
//             </p>
//             <div className="space-y-2">
//               <div className="flex items-start">
//                 <ArrowRight size={16} className="text-blue-600 mt-1 mr-2 flex-shrink-0" />
//                 <span>Focus initial marketing on bootstrapped founders and indie hackers</span>
//               </div>
//               <div className="flex items-start">
//                 <ArrowRight size={16} className="text-blue-600 mt-1 mr-2 flex-shrink-0" />
//                 <span>Prioritize integration with popular tools like Notion and Slack</span>
//               </div>
//               <div className="flex items-start">
//                 <ArrowRight size={16} className="text-blue-600 mt-1 mr-2 flex-shrink-0" />
//                 <span>Consider a freemium model with limited reports in the free tier</span>
//               </div>
//             </div>
//           </div>

//           <div className="text-center mt-8">
//             <button className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-3 px-8 rounded-lg shadow inline-flex items-center justify-center transition-colors">
//               Download Full Report
//             </button>
//             <p className="text-gray-500 text-sm mt-2">PDF format, includes all data and visualizations</p>
//           </div>
//         </main>
//       </div>
//     );
//   }

//   return null;
// };

// export default IdeasValidatorApp;

// import React, { useState } from "react";
// import { Search, RefreshCw, Download, Check, X, AlertTriangle } from "lucide-react";

// const SimpleIdeaValidator = () => {
//   const [step, setStep] = useState("input"); // input, loading, results
//   const [idea, setIdea] = useState("");

//   const handleSubmit = (e) => {
//     e.preventDefault();
//     if (!idea.trim()) return;
//     setStep("loading");

//     // Simulate API call
//     setTimeout(() => {
//       setStep("results");
//     }, 1500);
//   };

//   const handleReset = () => {
//     setStep("input");
//     setIdea("");
//   };

//   // Input Screen
//   if (step === "input") {
//     return (
//       <div className="min-h-screen bg-white flex flex-col items-center justify-center p-4">
//         <div className="w-full max-w-md">
//           <h1 className="text-2xl font-bold text-center text-gray-900 mb-2">Is Your Idea Any Good?</h1>
//           <p className="text-center text-gray-600 mb-8">Get a clear yes/no answer in 30 seconds</p>

//           <form onSubmit={handleSubmit} className="space-y-4">
//             <textarea
//               placeholder="Describe your business idea in a few sentences..."
//               className="w-full p-3 border border-gray-300 rounded-lg min-h-32 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
//               value={idea}
//               onChange={(e) => setIdea(e.target.value)}
//               required
//             />

//             <button type="submit" className="w-full bg-blue-600 hover:bg-blue-700 text-white font-bold py-3 rounded-lg transition-colors">
//               Validate My Idea Now
//             </button>
//           </form>

//           <div className="mt-8 text-center text-sm text-gray-500">
//             We scan thousands of social media conversations to determine if people actually want this
//           </div>
//         </div>
//       </div>
//     );
//   }

//   // Loading Screen
//   if (step === "loading") {
//     return (
//       <div className="min-h-screen bg-white flex flex-col items-center justify-center p-4">
//         <div className="w-full max-w-md text-center">
//           <RefreshCw size={48} className="mx-auto text-blue-600 animate-spin mb-4" />
//           <h2 className="text-xl font-bold text-gray-900 mb-2">Analyzing Your Idea</h2>
//           <p className="text-gray-600">Scanning what people are saying across Reddit, Twitter, and other platforms...</p>
//           <div className="w-full bg-gray-200 rounded-full h-2.5 mt-6">
//             <div className="bg-blue-600 h-2.5 rounded-full w-2/3 animate-pulse"></div>
//           </div>
//         </div>
//       </div>
//     );
//   }

//   // Results Screen
//   if (step === "results") {
//     // Demo result - in a real app this would come from API
//     const result = {
//       score: 83,
//       verdict: "YES",
//       evidence: [
//         {
//           quote: "I've been looking for exactly this kind of solution! The current options are all terrible.",
//           platform: "Reddit",
//           engagement: "126 upvotes",
//         },
//         {
//           quote: "Would pay good money for this if someone built it. Frustrating this doesn't exist yet.",
//           platform: "Twitter",
//           engagement: "89 likes",
//         },
//         {
//           quote: "Our company tried to solve this but it's harder than it looks. Definitely a real problem though.",
//           platform: "LinkedIn",
//           engagement: "34 comments",
//         },
//       ],
//       competitors: ["CompetitorA", "CompetitorB"],
//       nextSteps: [
//         "Focus on the mobile experience first",
//         "Target small business owners as early adopters",
//         "Price point around $29/month based on user comments",
//       ],
//     };

//     const getIcon = () => {
//       if (result.score >= 75) return <Check size={64} className="text-green-500" />;
//       if (result.score >= 50) return <AlertTriangle size={64} className="text-yellow-500" />;
//       return <X size={64} className="text-red-500" />;
//     };

//     const getColor = () => {
//       if (result.score >= 75) return "bg-green-100 text-green-800";
//       if (result.score >= 50) return "bg-yellow-100 text-yellow-800";
//       return "bg-red-100 text-red-800";
//     };

//     return (
//       <div className="min-h-screen bg-white p-4">
//         <div className="max-w-md mx-auto">
//           <div className="text-center mb-8">
//             {getIcon()}
//             <h1 className="text-3xl font-bold mt-4 mb-2">{result.verdict}</h1>
//             <p className="text-xl text-gray-600">People want this idea</p>
//             <div className={`mt-4 inline-block px-4 py-2 rounded-full text-xl font-bold ${getColor()}`}>{result.score}/100</div>
//           </div>

//           <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-6">
//             <h2 className="text-lg font-bold mb-4">What People Are Saying</h2>
//             <div className="space-y-4">
//               {result.evidence.map((item, index) => (
//                 <div key={index} className="bg-white p-3 rounded border border-gray-200">
//                   <p className="text-gray-800 mb-2">"{item.quote}"</p>
//                   <div className="text-sm text-gray-500">
//                     {item.platform} • {item.engagement}
//                   </div>
//                 </div>
//               ))}
//             </div>
//           </div>

//           <div className="bg-blue-50 border border-blue-100 rounded-lg p-4 mb-8">
//             <h2 className="text-lg font-bold mb-3">Next Steps</h2>
//             <ul className="space-y-2">
//               {result.nextSteps.map((step, index) => (
//                 <li key={index} className="flex items-start">
//                   <span className="text-blue-500 mr-2">•</span>
//                   <span>{step}</span>
//                 </li>
//               ))}
//             </ul>
//           </div>

//           <div className="flex space-x-3">
//             <button
//               onClick={handleReset}
//               className="flex-1 bg-gray-100 hover:bg-gray-200 text-gray-800 font-medium py-3 rounded-lg transition-colors"
//             >
//               Validate Another Idea
//             </button>
//             <button className="flex-1 bg-blue-600 hover:bg-blue-700 text-white font-medium py-3 rounded-lg transition-colors flex items-center justify-center">
//               <Download size={16} className="mr-2" />
//               Get Full Report
//             </button>
//           </div>

//           <p className="mt-8 text-center text-sm text-gray-500">Based on analysis of 3,427 social media conversations</p>
//         </div>
//       </div>
//     );
//   }
// };

// export default SimpleIdeaValidator;

// import React, { useState } from "react";
// import {
//   Search,
//   RefreshCw,
//   Download,
//   Check,
//   X,
//   AlertTriangle,
//   ChevronDown,
//   ChevronRight,
//   Twitter,
//   MessageCircle,
//   Bookmark,
//   Star,
// } from "lucide-react";

// const ImprovedSimpleValidator = () => {
//   const [step, setStep] = useState("input"); // input, loading, results
//   const [idea, setIdea] = useState("");
//   const [showAllPosts, setShowAllPosts] = useState(false);
//   const [activeTab, setActiveTab] = useState("all"); // all, twitter, reddit

//   const handleSubmit = (e) => {
//     e.preventDefault();
//     if (!idea.trim()) return;
//     setStep("loading");

//     // Simulate API call
//     setTimeout(() => {
//       setStep("results");
//     }, 1500);
//   };

//   const handleReset = () => {
//     setStep("input");
//     setIdea("");
//     setShowAllPosts(false);
//   };

//   // Input Screen
//   if (step === "input") {
//     return (
//       <div className="min-h-screen bg-white flex flex-col items-center justify-center p-4">
//         <div className="w-full max-w-md">
//           <h1 className="text-2xl font-bold text-center text-gray-900 mb-2">Is Your Idea Any Good?</h1>
//           <p className="text-center text-gray-600 mb-8">Get a clear yes/no answer in 30 seconds</p>

//           <form onSubmit={handleSubmit} className="space-y-4">
//             <textarea
//               placeholder="Describe your business idea in a few sentences..."
//               className="w-full p-3 border border-gray-300 rounded-lg min-h-32 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
//               value={idea}
//               onChange={(e) => setIdea(e.target.value)}
//               required
//             />

//             <button type="submit" className="w-full bg-blue-600 hover:bg-blue-700 text-white font-bold py-3 rounded-lg transition-colors">
//               Validate My Idea Now
//             </button>
//           </form>

//           <div className="mt-8 text-center text-sm text-gray-500">
//             We scan thousands of social media conversations to determine if people actually want this
//           </div>
//         </div>
//       </div>
//     );
//   }

//   // Loading Screen
//   if (step === "loading") {
//     return (
//       <div className="min-h-screen bg-white flex flex-col items-center justify-center p-4">
//         <div className="w-full max-w-md text-center">
//           <RefreshCw size={48} className="mx-auto text-blue-600 animate-spin mb-4" />
//           <h2 className="text-xl font-bold text-gray-900 mb-2">Analyzing Your Idea</h2>
//           <p className="text-gray-600">Scanning what people are saying across Reddit, Twitter, and other platforms...</p>
//           <div className="w-full bg-gray-200 rounded-full h-2.5 mt-6">
//             <div className="bg-blue-600 h-2.5 rounded-full w-2/3 animate-pulse"></div>
//           </div>
//         </div>
//       </div>
//     );
//   }

//   // Results Screen
//   if (step === "results") {
//     // Demo result - in a real app this would come from API
//     const result = {
//       score: 83,
//       verdict: "YES",
//       twitterPosts: [
//         {
//           id: 1,
//           author: "productguru",
//           verified: true,
//           content:
//             "I've been looking for exactly this kind of solution for months! The current options are all terrible and overpriced. Would switch in a heartbeat.",
//           likes: 126,
//           retweets: 42,
//           date: "2 days ago",
//         },
//         {
//           id: 2,
//           author: "startupfounder",
//           verified: false,
//           content:
//             "Would pay good money for this if someone built it properly. Frustrating this doesn't exist yet in a user-friendly format.",
//           likes: 89,
//           retweets: 17,
//           date: "1 week ago",
//         },
//         {
//           id: 3,
//           author: "techinfluencer",
//           verified: true,
//           content:
//             "Every time I try to solve this problem I end up with a clunky workaround. A dedicated solution would save me hours every week.",
//           likes: 215,
//           retweets: 54,
//           date: "3 days ago",
//         },
//         {
//           id: 4,
//           author: "product_manager",
//           verified: false,
//           content:
//             "My team has been cobbling together multiple tools to solve this. Would consolidate in a heartbeat if something better came along.",
//           likes: 73,
//           retweets: 12,
//           date: "5 days ago",
//         },
//         {
//           id: 5,
//           author: "ux_designer",
//           verified: false,
//           content: "The UX of existing solutions for this is absolutely terrible. Huge opportunity for someone to do it right.",
//           likes: 104,
//           retweets: 31,
//           date: "1 day ago",
//         },
//       ],
//       redditPosts: [
//         {
//           id: 1,
//           author: "innovation_junkie",
//           subreddit: "r/productivity",
//           content:
//             "Does anyone know a good solution for this problem? I'm tired of the manual workarounds and existing apps are just not cutting it.",
//           upvotes: 342,
//           comments: 78,
//           date: "3 days ago",
//         },
//         {
//           id: 2,
//           author: "efficiency_seeker",
//           subreddit: "r/startups",
//           content:
//             "Our company tried to solve this internally but it's harder than it looks. Definitely a real problem with market potential though.",
//           upvotes: 156,
//           comments: 34,
//           date: "1 week ago",
//         },
//         {
//           id: 3,
//           author: "tech_enthusiast",
//           subreddit: "r/technology",
//           content:
//             "I can't believe nobody has built a good solution for this yet. The market is wide open for someone who can execute well.",
//           upvotes: 287,
//           comments: 52,
//           date: "4 days ago",
//         },
//         {
//           id: 4,
//           author: "small_biz_owner",
//           subreddit: "r/smallbusiness",
//           content: "This is eating up so much of my time every week. Would gladly pay monthly for something that automates this process.",
//           upvotes: 192,
//           comments: 45,
//           date: "2 days ago",
//         },
//         {
//           id: 5,
//           author: "software_dev",
//           subreddit: "r/programming",
//           content: "I've looked at building this solution myself, but it would take months. Surprised nobody has tackled it properly yet.",
//           upvotes: 218,
//           comments: 63,
//           date: "6 days ago",
//         },
//       ],
//       competitors: [
//         { name: "CompetitorA", marketShare: "32%", weakness: "Poor user experience" },
//         { name: "CompetitorB", marketShare: "18%", weakness: "Missing key features" },
//         { name: "CompetitorC", marketShare: "12%", weakness: "Overpriced for value" },
//       ],
//       nextSteps: [
//         "Focus on the mobile experience first - mentioned in 62% of discussions",
//         "Target small business owners as early adopters - most vocal about the problem",
//         "Price point around $29/month based on user comments about value",
//       ],
//     };

//     const getIcon = () => {
//       if (result.score >= 75) return <Check size={64} className="text-green-500" />;
//       if (result.score >= 50) return <AlertTriangle size={64} className="text-yellow-500" />;
//       return <X size={64} className="text-red-500" />;
//     };

//     const getColor = () => {
//       if (result.score >= 75) return "bg-green-100 text-green-800";
//       if (result.score >= 50) return "bg-yellow-100 text-yellow-800";
//       return "bg-red-100 text-red-800";
//     };

//     const visibleTwitterPosts = showAllPosts ? result.twitterPosts : result.twitterPosts.slice(0, 2);
//     const visibleRedditPosts = showAllPosts ? result.redditPosts : result.redditPosts.slice(0, 2);

//     // Filter posts based on active tab
//     const getVisiblePosts = () => {
//       if (activeTab === "twitter") return visibleTwitterPosts;
//       if (activeTab === "reddit") return visibleRedditPosts;
//       // For 'all' tab, we'll alternate between Twitter and Reddit posts
//       const combined = [];
//       const maxLength = Math.max(visibleTwitterPosts.length, visibleRedditPosts.length);
//       for (let i = 0; i < maxLength; i++) {
//         if (i < visibleTwitterPosts.length) combined.push({ ...visibleTwitterPosts[i], type: "twitter" });
//         if (i < visibleRedditPosts.length) combined.push({ ...visibleRedditPosts[i], type: "reddit" });
//       }
//       return combined.slice(0, showAllPosts ? combined.length : 4);
//     };

//     const totalPosts = result.twitterPosts.length + result.redditPosts.length;
//     const visiblePosts = getVisiblePosts();
//     const hiddenCount = totalPosts - visiblePosts.length;

//     return (
//       <div className="min-h-screen bg-white p-4">
//         <div className="max-w-xl mx-auto">
//           {/* Verdict Section */}
//           <div className="text-center mb-8">
//             {getIcon()}
//             <h1 className="text-3xl font-bold mt-4 mb-2">{result.verdict}</h1>
//             <p className="text-xl text-gray-600">People want this idea</p>
//             <div className={`mt-4 inline-block px-4 py-2 rounded-full text-xl font-bold ${getColor()}`}>{result.score}/100</div>
//           </div>

//           {/* Social Media Evidence Section */}
//           <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-6">
//             <div className="flex items-center justify-between mb-4">
//               <h2 className="text-lg font-bold">What People Are Saying</h2>

//               <div className="flex space-x-1 bg-gray-200 rounded-lg p-1">
//                 <button
//                   className={`px-3 py-1 text-sm rounded-md ${activeTab === "all" ? "bg-white shadow" : "hover:bg-gray-100"}`}
//                   onClick={() => setActiveTab("all")}
//                 >
//                   All
//                 </button>
//                 <button
//                   className={`px-3 py-1 text-sm rounded-md flex items-center ${
//                     activeTab === "twitter" ? "bg-white shadow" : "hover:bg-gray-100"
//                   }`}
//                   onClick={() => setActiveTab("twitter")}
//                 >
//                   <Twitter size={14} className="mr-1" />
//                   Twitter
//                 </button>
//                 <button
//                   className={`px-3 py-1 text-sm rounded-md flex items-center ${
//                     activeTab === "reddit" ? "bg-white shadow" : "hover:bg-gray-100"
//                   }`}
//                   onClick={() => setActiveTab("reddit")}
//                 >
//                   <MessageCircle size={14} className="mr-1" />
//                   Reddit
//                 </button>
//               </div>
//             </div>

//             <div className="space-y-4">
//               {visiblePosts.map((post, index) => {
//                 if (post.type === "reddit" || post.subreddit) {
//                   // Reddit post
//                   const redditPost = post.type === "reddit" ? post : post;
//                   return (
//                     <div
//                       key={`reddit-${redditPost.id}`}
//                       className="bg-white p-4 rounded-lg border border-gray-200 hover:border-orange-200 hover:bg-orange-50 transition-colors"
//                     >
//                       <div className="flex items-center mb-2">
//                         <div className="h-8 w-8 rounded-full bg-orange-100 flex items-center justify-center">
//                           <MessageCircle size={16} className="text-orange-600" />
//                         </div>
//                         <div className="ml-2">
//                           <div className="font-medium text-gray-900">u/{redditPost.author}</div>
//                           <div className="text-xs text-gray-500">{redditPost.subreddit}</div>
//                         </div>
//                         <div className="ml-auto text-xs text-gray-500">{redditPost.date}</div>
//                       </div>
//                       <p className="text-gray-800 mb-3">{redditPost.content}</p>
//                       <div className="flex text-xs text-gray-500">
//                         <span>{redditPost.upvotes} upvotes</span>
//                         <span className="mx-2">•</span>
//                         <span>{redditPost.comments} comments</span>
//                       </div>
//                     </div>
//                   );
//                 } else {
//                   // Twitter post
//                   const twitterPost = post.type === "twitter" ? post : post;
//                   return (
//                     <div
//                       key={`twitter-${twitterPost.id}`}
//                       className="bg-white p-4 rounded-lg border border-gray-200 hover:border-blue-200 hover:bg-blue-50 transition-colors"
//                     >
//                       <div className="flex items-center mb-2">
//                         <div className="h-8 w-8 rounded-full bg-blue-100 flex items-center justify-center">
//                           <Twitter size={16} className="text-blue-600" />
//                         </div>
//                         <div className="ml-2">
//                           <div className="font-medium text-gray-900">@{twitterPost.author}</div>
//                           <div className="text-xs text-gray-500">Twitter</div>
//                         </div>
//                         <div className="ml-auto text-xs text-gray-500">{twitterPost.date}</div>
//                       </div>
//                       <p className="text-gray-800 mb-3">{twitterPost.content}</p>
//                       <div className="flex text-xs text-gray-500">
//                         <span>{twitterPost.likes} likes</span>
//                         <span className="mx-2">•</span>
//                         <span>{twitterPost.retweets} retweets</span>
//                       </div>
//                     </div>
//                   );
//                 }
//               })}
//             </div>

//             {hiddenCount > 0 && (
//               <button
//                 onClick={() => setShowAllPosts(true)}
//                 className="mt-4 w-full py-2 border border-gray-300 rounded-lg text-gray-700 flex items-center justify-center hover:bg-gray-50"
//               >
//                 Show {hiddenCount} more posts <ChevronDown size={16} className="ml-1" />
//               </button>
//             )}

//             {showAllPosts && (
//               <button
//                 onClick={() => setShowAllPosts(false)}
//                 className="mt-4 w-full py-2 border border-gray-300 rounded-lg text-gray-700 flex items-center justify-center hover:bg-gray-50"
//               >
//                 Show fewer posts <ChevronRight size={16} className="ml-1 transform rotate-90" />
//               </button>
//             )}
//           </div>

//           {/* Competitors Section */}
//           <div className="bg-white border border-gray-200 rounded-lg p-4 mb-6">
//             <h2 className="text-lg font-bold mb-4">Competitive Landscape</h2>
//             <div className="space-y-3">
//               {result.competitors.map((competitor, index) => (
//                 <div key={index} className="flex items-center justify-between py-2 border-b border-gray-100 last:border-0">
//                   <div>
//                     <div className="font-medium">{competitor.name}</div>
//                     <div className="text-sm text-gray-500">{competitor.weakness}</div>
//                   </div>
//                   <div className="text-sm font-medium">{competitor.marketShare}</div>
//                 </div>
//               ))}
//             </div>
//           </div>

//           {/* Next Steps Section */}
//           <div className="bg-blue-50 border border-blue-100 rounded-lg p-4 mb-8">
//             <h2 className="text-lg font-bold mb-3">What You Should Do Next</h2>
//             <ul className="space-y-3">
//               {result.nextSteps.map((step, index) => (
//                 <li key={index} className="flex items-start">
//                   <span className="text-blue-500 mr-2">•</span>
//                   <span>{step}</span>
//                 </li>
//               ))}
//             </ul>
//           </div>

//           {/* Action Buttons */}
//           <div className="flex flex-col space-y-3">
//             <button className="w-full bg-blue-600 hover:bg-blue-700 text-white font-bold py-4 rounded-lg transition-colors flex items-center justify-center text-lg">
//               <Download size={20} className="mr-2" />
//               Download Complete Report (PDF)
//             </button>

//             <div className="flex space-x-3">
//               <button
//                 onClick={handleReset}
//                 className="flex-1 bg-gray-100 hover:bg-gray-200 text-gray-800 font-medium py-3 rounded-lg transition-colors"
//               >
//                 Validate Another Idea
//               </button>

//               <button className="flex-1 border border-blue-200 bg-blue-50 hover:bg-blue-100 text-blue-700 font-medium py-3 rounded-lg transition-colors flex items-center justify-center">
//                 <Bookmark size={16} className="mr-2" />
//                 Save This Result
//               </button>
//             </div>
//           </div>

//           <p className="mt-8 text-center text-sm text-gray-500">Based on analysis of 3,427 social media conversations</p>
//         </div>
//       </div>
//     );
//   }
// };

// export default ImprovedSimpleValidator;
